import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Flame, TrendingUp } from 'lucide-react';
import { useCountdown } from '../hooks/useCountdown';
interface UrgencyModuleProps {
  variant?: 'light' | 'dark';
}
export const UrgencyModule = ({
  variant = 'light'
}: UrgencyModuleProps) => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const isDark = variant === 'dark';
  const cuposTaken = 68;
  return <div className={`${isDark ? 'bg-navy' : 'bg-white'} py-4 md:py-6 px-3 md:px-4`} data-id="element-436">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="max-w-3xl mx-auto" data-id="element-437">
        {/* Outer pulsing border */}
        <div className="relative" data-id="element-438">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-danger via-gold to-danger rounded-2xl opacity-75 blur-sm animate-pulse" data-id="element-439"></div>

          <div className="relative bg-gradient-to-br from-danger via-danger-dark to-danger text-white rounded-2xl px-4 py-3 md:px-6 md:py-4 shadow-2xl overflow-hidden" data-id="element-440">
            {/* Background pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvc3ZnPg==')] opacity-30" data-id="element-441"></div>

            <div className="relative z-10" data-id="element-442">
              {/* Top row: warning + countdown */}
              <div className="flex items-center justify-between gap-3 md:gap-4 mb-2.5 md:mb-3" data-id="element-443">
                {/* Warning */}
                <div className="flex items-center gap-2 min-w-0" data-id="element-444">
                  <motion.div animate={{
                  rotate: [0, -10, 10, -10, 0]
                }} transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  repeatDelay: 2
                }} className="flex-shrink-0" data-id="element-445">
                    <Flame className="w-5 h-5 md:w-7 md:h-7 text-gold" data-id="element-446" />
                  </motion.div>
                  <div className="min-w-0" data-id="element-447">
                    <div className="font-black text-xs md:text-base leading-tight uppercase tracking-wide" data-id="element-448">
                      Oferta por tiempo limitado
                    </div>
                    <div className="text-[9px] md:text-xs text-white/80 uppercase tracking-wider font-bold leading-tight" data-id="element-449">
                      Termina en
                    </div>
                  </div>
                </div>

                {/* Countdown */}
                <div className="flex items-center gap-1 md:gap-1.5 text-base md:text-2xl font-black tabular-nums tracking-tight flex-shrink-0" data-id="element-450">
                  <div className="bg-black/40 backdrop-blur-sm px-1.5 md:px-2.5 py-1 md:py-1.5 rounded-md min-w-[2.3ch] md:min-w-[2.8ch] text-center shadow-inner" data-id="element-451">
                    {hours}
                  </div>
                  <span className="text-gold/80" data-id="element-452">:</span>
                  <div className="bg-black/40 backdrop-blur-sm px-1.5 md:px-2.5 py-1 md:py-1.5 rounded-md min-w-[2.3ch] md:min-w-[2.8ch] text-center shadow-inner" data-id="element-453">
                    {minutes}
                  </div>
                  <span className="text-gold/80" data-id="element-454">:</span>
                  <div className="bg-black/40 backdrop-blur-sm px-1.5 md:px-2.5 py-1 md:py-1.5 rounded-md min-w-[2.3ch] md:min-w-[2.8ch] text-center shadow-inner animate-pulse" data-id="element-455">
                    {seconds}
                  </div>
                </div>
              </div>

              {/* Cupos bar */}
              <div className="mb-2.5 md:mb-3" data-id="element-456">
                <div className="flex justify-between items-center mb-1 text-[10px] md:text-xs font-bold uppercase tracking-wider" data-id="element-457">
                  <span className="flex items-center gap-1" data-id="element-458">
                    <span className="inline-block w-1.5 h-1.5 bg-gold rounded-full animate-pulse" data-id="element-459"></span>
                    Cupos restantes
                  </span>
                  <span className="text-gold" data-id="element-460">{cuposTaken}% TOMADOS</span>
                </div>
                <div className="h-2 md:h-2.5 bg-black/40 rounded-full overflow-hidden shadow-inner" data-id="element-461">
                  <motion.div initial={{
                  width: 0
                }} whileInView={{
                  width: `${cuposTaken}%`
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 1.2,
                  ease: 'easeOut'
                }} className="h-full bg-gradient-to-r from-gold via-gold-light to-gold rounded-full shadow-[0_0_10px_rgba(212,162,76,0.6)]" data-id="element-462"></motion.div>
                </div>
              </div>

              {/* Warning message */}
              <div className="flex items-start gap-2 bg-black/30 rounded-lg px-2.5 py-2 md:px-3 md:py-2 backdrop-blur-sm border border-white/10" data-id="element-463">
                <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-gold flex-shrink-0 mt-0.5" data-id="element-464" />
                <p className="text-[11px] md:text-sm leading-snug font-medium" data-id="element-465">
                  Cuando se acaben los cupos el precio{' '}
                  <span className="text-gold font-black" data-id="element-466">sube $10.000</span> y{' '}
                  <span className="underline decoration-gold decoration-2 underline-offset-2 font-black" data-id="element-467">
                    NO vuelve a bajar
                  </span>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
};